import jsPDF from 'jspdf';
import {loadCustomFont, tryLoadLogoImage} from './pdfUtils';
import {buildXRechnungXml, embedXmlInPdf, formatValidationResult, InvoiceResult, mapLegacyInvoiceData, validateXRechnungData, XRechnungOptions,} from './xrechnung';

/**
 * Invoice line item
 */
export interface InvoiceLineItem {
    description: string;
    note?: string; // Additional note/description shown below the item name
    quantity: number;
    unit?: string;
    unitPrice?: number;
    total: number;
}

/**
 * Emitter (company) information for PDF generation and XRechnung
 */
export interface InvoiceEmitterInfo {
    name: string;
    street: string;
    postal_code: string;
    city: string;
    country_code?: string;
    // Contact
    contact_phone?: string;
    contact_email?: string;
    // Additional info
    website?: string;
    fax?: string;
    management?: string; // Geschäftsführung (pipe-separated names)
    court_registration?: string; // Amtsgericht/HRB
    po_box?: string; // Postfach
    logo_path?: string;
    // Bank details
    iban: string;
    bic?: string;
    bank_name?: string;
    // Tax - required for XRechnung
    tax_number?: string;
    vat_id?: string;      // USt-IdNr (e.g., DE123456789)
    company_id?: string;  // HRB number (e.g., HRB 760818)
}

/**
 * Invoice data structure
 */
export interface InvoiceData {
    invoiceNumber: string;
    invoiceDate: Date;
    customer: {
        name: string;
        street: string;
        zipCity: string;
        fullContact?: string; // If set, use this as full address instead of structured fields
    };
    emitter?: InvoiceEmitterInfo; // Company info for PDF header/footer
    objekt: string;
    durchgefuehrteArbeiten: string;
    durchgefuehrtAm: Date;
    lineItems: InvoiceLineItem[];
    vatRate: number; // e.g., 19 for 19%
    // Skonto (Cash Discount)
    skonto_percentage?: number;
    skonto_days?: number;
    // Payment terms
    payment_days?: number; // Days until payment is due (default: 30)
    // Stornorechnung (Cancellation Invoice) fields
    is_storno?: boolean;
    storno_of_invoice_number?: string; // Original invoice number (for header: "Stornorechnung Nr. X zur Rechnung Nr. Y")
}

/**
 * Company information constants
 */
const COMPANY_INFO = {
    name: 'Böhringer Technik GmbH',
    website: 'www.boehringer.net',
    senderLine: 'Böhringer Technik GmbH | Postfach 1251 | 74072 Heilbronn',
    footer: {
        line1: 'Böhringer Technik GmbH | Wilhelmstraße 14, 74072 Heilbronn',
        line2: 'Fon: 0 71 31 / 74 57 0 - 0 | Fax: 0 71 31 / 74 57 0 - 10 | info@boehringer.net',
        line3: 'Bankverbindung: VBU Volksbank im Unterland eG · IBAN: DE88 6206 3263 0204 2630 00 · BIC: GENODES1VLS',
        line4: 'Geschäftsführung: Otto Böhringer | Susanne Krueger | Philipp Clemens',
        line5: 'Amtsgericht Stuttgart HRB 760818 | St.-Nr.: 65201/64287',
    },
};

/**
 * Page layout constants (in mm)
 * A4 page is 210mm wide x 297mm tall
 */
const PAGE_LAYOUT = {
    leftMargin: 22,      // Left margin (was 20)
    rightEdge: 188,      // Right edge position (was 190)
    contentWidth: 166,   // Available content width (185 - 25 = 160)
    logoX: 152,          // Logo X position (was 155)
};

/**
 * Format date in German locale (DD. Month YYYY)
 */
const formatDateGerman = (date: Date): string => {
    return date.toLocaleDateString('de-DE', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
    });
};

/**
 * Format date short (DD.MM.YYYY)
 */
const formatDateShort = (date: Date): string => {
    return date.toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    });
};

/**
 * Format currency in Euro
 */
const formatCurrency = (amount: number): string => {
    return amount.toLocaleString('de-DE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }) + ' \u20AC';
};

/**
 * Add header section with website and logo
 */
const addHeader = async (doc: jsPDF, emitter?: InvoiceEmitterInfo): Promise<number> => {
    // Website URL top left
    doc.setFontSize(10);
    doc.setFont('Dax', 'normal');
    doc.setTextColor(150, 150, 150);
    const website = emitter?.website || COMPANY_INFO.website;
    doc.text(website, PAGE_LAYOUT.leftMargin, 25);

    // Logo top right
    try {
        const logoResult = await tryLoadLogoImage();
        if (logoResult) {
            doc.addImage(logoResult.dataUrl, logoResult.format, PAGE_LAYOUT.logoX, 15, 40, 40);
        }
    } catch (error) {
        console.warn('Could not load logo:', error);
    }

    return 70;
};

/**
 * Build sender line from emitter data
 */
const buildSenderLine = (emitter?: InvoiceEmitterInfo): string => {
    if (!emitter) {
        return COMPANY_INFO.senderLine;
    }
    const parts = [emitter.name];
    if (emitter.po_box || emitter.street) {
        parts.push(emitter.po_box || emitter.street);
    }
    parts.push(`${emitter.postal_code} ${emitter.city}`);
    return parts.join(' | ');
};

/**
 * Add sender line and customer address
 */
const addAddressBlock = (doc: jsPDF, customer: InvoiceData['customer'], currentY: number, emitter?: InvoiceEmitterInfo): number => {
    let y = currentY;

    // Sender line (small, above customer address)
    doc.setFontSize(8);
    doc.setFont('Dax', 'normal');
    doc.setTextColor(100, 100, 100);
    const senderLine = buildSenderLine(emitter);
    doc.text(senderLine, PAGE_LAYOUT.leftMargin, y);
    y += 8;

    // Customer address
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);

    // Check if fullContact is provided (use it instead of structured fields)
    if (customer.fullContact && customer.fullContact.trim()) {
        // Split full contact by newlines and render each line
        const lines = customer.fullContact.split('\n').filter(line => line.trim());
        for (const line of lines) {
            doc.text(line.trim(), PAGE_LAYOUT.leftMargin, y);
            y += 5;
        }
        y += 10; // Add spacing after address block
    } else {
        // Use structured fields
        // Customer name
        doc.text(customer.name, PAGE_LAYOUT.leftMargin, y);
        y += 5;

        // Street
        doc.text(customer.street, PAGE_LAYOUT.leftMargin, y);
        y += 5;

        // ZIP and City
        doc.text(customer.zipCity, PAGE_LAYOUT.leftMargin, y);
        y += 15;
    }

    return y;
};

/**
 * Add invoice date on the right side
 */
const addInvoiceDate = (doc: jsPDF, date: Date, y: number): void => {
    doc.setFontSize(11);
    doc.setFont('Dax', 'normal');
    doc.setTextColor(0, 0, 0);
    const dateStr = formatDateGerman(date);
    const dateWidth = doc.getTextWidth(dateStr);
    doc.text(dateStr, PAGE_LAYOUT.rightEdge - dateWidth, y);
};

/**
 * Add invoice title and details
 */
const addInvoiceDetails = (doc: jsPDF, data: InvoiceData, currentY: number): number => {
    let y = currentY;

    // Invoice number title
    doc.setFontSize(14);
    doc.setFont('Dax', 'normal', 'bold');
    doc.setTextColor(0, 0, 0);
    if (data.is_storno && data.storno_of_invoice_number) {
        // Stornorechnung header: "Stornorechnung Nr. X zur Rechnung Nr. Y"
        doc.text(`Stornorechnung Nr. ${data.invoiceNumber} zur Rechnung Nr. ${data.storno_of_invoice_number}`, PAGE_LAYOUT.leftMargin, y);
        y += 10;
    } else {
        doc.text(`Rechnung Nr. ${data.invoiceNumber}`, PAGE_LAYOUT.leftMargin, y);
        doc.setFont('Dax', 'normal');
        y += 12;
    }

    // Object details
    doc.setFontSize(11);

    // Objekt
    doc.setFont('Dax', 'normal', 'bold');
    doc.text('Objekt: ', PAGE_LAYOUT.leftMargin, y);
    const objektLabelWidth = doc.getTextWidth('Objekt: ');
    doc.setFont('Dax', 'normal');
    doc.text(data.objekt, PAGE_LAYOUT.leftMargin + objektLabelWidth, y);
    y += 5;

    // Durchgeführte Arbeiten (handle multiline)
    doc.setFont('Dax', 'normal', 'bold');
    const arbeitenLabel = 'Durchgeführte Arbeiten: ';
    doc.text(arbeitenLabel, PAGE_LAYOUT.leftMargin, y);
    const arbeitenLabelWidth = doc.getTextWidth(arbeitenLabel);
    doc.setFont('Dax', 'normal');

    // Calculate available width for text after label
    const availableWidth = PAGE_LAYOUT.contentWidth - arbeitenLabelWidth;
    const arbeitenLines = doc.splitTextToSize(data.durchgefuehrteArbeiten, availableWidth);

    // First line after label
    if (arbeitenLines.length > 0) {
        doc.text(arbeitenLines[0], PAGE_LAYOUT.leftMargin + arbeitenLabelWidth, y);
    }
    y += 5;

    // Subsequent lines with indent to align with first line
    for (let i = 1; i < arbeitenLines.length; i++) {
        doc.text(arbeitenLines[i], PAGE_LAYOUT.leftMargin + arbeitenLabelWidth, y);
        y += 5;
    }

    // Durchgeführt am
    doc.setFont('Dax', 'normal', 'bold');
    doc.text('Durchgeführt am: ', PAGE_LAYOUT.leftMargin, y);
    const datumLabelWidth = doc.getTextWidth('Durchgeführt am: ');
    doc.setFont('Dax', 'normal');
    doc.text(formatDateShort(data.durchgefuehrtAm), PAGE_LAYOUT.leftMargin + datumLabelWidth, y);
    y += 15;

    return y;
};

/**
 * Add greeting text
 */
const addGreeting = (doc: jsPDF, currentY: number): number => {
    let y = currentY;

    doc.setFontSize(11);
    doc.setFont('Dax', 'normal');
    doc.setTextColor(0, 0, 0);
    doc.text('Sehr geehrte Damen und Herren,', PAGE_LAYOUT.leftMargin, y);
    y += 5;
    doc.text('wir stellen Ihnen für die Durchführung in Rechnung:', PAGE_LAYOUT.leftMargin, y);
    y += 10;

    return y;
};

/**
 * Add line items table
 */
const addLineItems = (doc: jsPDF, items: InvoiceLineItem[], currentY: number): number => {
    let y = currentY;
    const col1X = PAGE_LAYOUT.leftMargin;    // Description
    const col2X = 115;   // Unit price
    const rightEdge = PAGE_LAYOUT.rightEdge; // Right edge for full-width lines

    doc.setFontSize(11);
    doc.setFont('Dax', 'normal');
    doc.setTextColor(0, 0, 0);

    for (const item of items) {
        // Description with quantity
        const description = `${item.quantity.toLocaleString('de-DE')} x ${item.description}`;
        doc.text(description, col1X, y);

        // Unit price (if available)
        if (item.unitPrice !== undefined) {
            doc.text(formatCurrency(item.unitPrice) + (item.unit ? '/' + item.unit : ''), col2X, y);
        }

        // Total (right-aligned)
        const totalStr = formatCurrency(item.total);
        const totalWidth = doc.getTextWidth(totalStr);
        doc.text(totalStr, rightEdge - totalWidth, y);

        y += 5;

        // Add note if present (smaller font, left padding, space below)
        if (item.note && item.note.trim()) {
            doc.setFontSize(9);
            doc.setTextColor(80, 80, 80);
            doc.text(item.note, col1X + 5, y); // 5px left padding
            doc.setFontSize(11);
            doc.setTextColor(0, 0, 0);
            y += 6; // Space after note
        } else {
            y += 2; // Normal spacing between items
        }
    }

    // Add full-width horizontal line under all positions
    y -= 2;
    doc.setLineWidth(0.3);
    doc.setDrawColor(0, 0, 0);
    doc.line(col1X, y, rightEdge, y);
    y += 0;

    return y;
};

/**
 * Add totals section
 */
const addTotals = (doc: jsPDF, subtotal: number, vatRate: number, currentY: number): number => {
    let y = currentY;
    const labelX = PAGE_LAYOUT.leftMargin;
    const valueX = PAGE_LAYOUT.rightEdge; // Right-aligned

    doc.setFontSize(11);
    doc.setFont('Dax', 'normal');
    doc.setTextColor(0, 0, 0);

    const vatAmount = subtotal * (vatRate / 100);
    const total = subtotal + vatAmount;

    // Zwischensumme
    doc.text('Zwischensumme:', labelX, y);
    const subtotalStr = formatCurrency(subtotal);
    doc.text(subtotalStr, valueX - doc.getTextWidth(subtotalStr), y);
    y += 7;

    // MwSt
    doc.text(`zzgl. ${vatRate}% MwSt.`, labelX, y);
    const vatStr = formatCurrency(vatAmount);
    doc.text(vatStr, valueX - doc.getTextWidth(vatStr), y);
    // Add full-width line above total
    doc.setLineWidth(0.3);
    doc.setDrawColor(0, 0, 0);
    doc.line(labelX, y + 3, valueX, y + 3);
    y += 8;

    // Gesamt
    doc.setFont('Dax', 'normal', 'bold');
    doc.text('Gesamt:', labelX, y);
    const totalStr = formatCurrency(total);
    doc.text(totalStr, valueX - doc.getTextWidth(totalStr), y);
    doc.setFont('Dax', 'normal');
    y += 15;

    return y;
};

/**
 * Add payment terms
 */
const addPaymentTerms = (doc: jsPDF, currentY: number, skonto_percentage?: number, skonto_days?: number, payment_days?: number): number => {
    let y = currentY;

    doc.setFontSize(11);
    doc.setFont('Dax', 'normal');
    doc.setTextColor(0, 0, 0);

    // Default payment days to 30
    const effectivePaymentDays = payment_days ?? 30;

    // Generate payment terms text based on Skonto settings
    let paymentText: string;
    if (skonto_percentage && skonto_days && skonto_percentage > 0 && skonto_days > 0) {
        // Format percentage with German locale (comma as decimal separator)
        const formattedPercentage = skonto_percentage.toLocaleString('de-DE', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        });
        paymentText = `Zahlungsbedingungen: ${formattedPercentage} % Skonto bei Zahlung innerhalb von ${skonto_days} Tagen; netto ohne Abzug innerhalb von ${effectivePaymentDays} Tagen.`;
    } else {
        paymentText = `Zahlungsbedingungen: Zahlbar innerhalb von ${effectivePaymentDays} Tagen ohne Abzug.`;
    }

    // Wrap text to match content width
    const maxWidth = PAGE_LAYOUT.contentWidth;
    const lines = doc.splitTextToSize(paymentText, maxWidth);
    for (const line of lines) {
        doc.text(line, PAGE_LAYOUT.leftMargin, y);
        y += 5;
    }
    y += 5;

    return y;
};

/**
 * Build footer lines from emitter data
 */
const buildFooterLines = (emitter?: InvoiceEmitterInfo): { line1Company: string; line1Address: string; line2: string; line3: string; line4: string; line5: string } => {
    if (!emitter) {
        return {
            line1Company: 'Böhringer Technik GmbH',
            line1Address: ' | Wilhelmstraße 14, 74072 Heilbronn',
            line2: COMPANY_INFO.footer.line2,
            line3: COMPANY_INFO.footer.line3,
            line4: COMPANY_INFO.footer.line4,
            line5: COMPANY_INFO.footer.line5,
        };
    }

    // Line 1: Company name | Address
    const line1Address = ` | ${emitter.street}, ${emitter.postal_code} ${emitter.city}`;

    // Line 2: Phone | Fax | Email
    const contactParts: string[] = [];
    if (emitter.contact_phone) contactParts.push(`Fon: ${emitter.contact_phone}`);
    if (emitter.fax) contactParts.push(`Fax: ${emitter.fax}`);
    if (emitter.contact_email) contactParts.push(emitter.contact_email);
    const line2 = contactParts.join(' | ');

    // Line 3: Bank details
    const bankParts: string[] = [];
    if (emitter.bank_name) bankParts.push(`Bankverbindung: ${emitter.bank_name}`);
    bankParts.push(`IBAN: ${emitter.iban}`);
    if (emitter.bic) bankParts.push(`BIC: ${emitter.bic}`);
    const line3 = bankParts.join(' · ');

    // Line 4: Management
    const line4 = emitter.management ? `Geschäftsführung: ${emitter.management}` : '';

    // Line 5: Court registration | Tax number
    const regParts: string[] = [];
    if (emitter.court_registration) regParts.push(emitter.court_registration);
    if (emitter.tax_number) regParts.push(`St.-Nr.: ${emitter.tax_number}`);
    const line5 = regParts.join(' | ');

    return {
        line1Company: emitter.name,
        line1Address,
        line2,
        line3,
        line4,
        line5,
    };
};

/**
 * Add footer with company details
 */
const addFooter = (doc: jsPDF, emitter?: InvoiceEmitterInfo): void => {
    const pageHeight = doc.internal.pageSize.height;
    const footerY = pageHeight - 35;

    doc.setFontSize(8);
    doc.setFont('Dax', 'normal');
    doc.setTextColor(100, 100, 100);

    let y = footerY;
    const lineHeight = 4;

    const footer = buildFooterLines(emitter);

    doc.setFont('Dax', 'normal', 'bold');
    doc.text(footer.line1Company, PAGE_LAYOUT.leftMargin, y);
    const companyWidth = doc.getTextWidth(footer.line1Company);

    doc.setTextColor(100, 100, 100);
    doc.setFont('Dax', 'normal');
    doc.text(footer.line1Address, PAGE_LAYOUT.leftMargin + companyWidth, y);
    y += lineHeight;

    if (footer.line2) {
        doc.text(footer.line2, PAGE_LAYOUT.leftMargin, y);
        y += lineHeight;
    }

    if (footer.line3) {
        doc.text(footer.line3, PAGE_LAYOUT.leftMargin, y);
        y += lineHeight;
    }

    if (footer.line4) {
        doc.text(footer.line4, PAGE_LAYOUT.leftMargin, y);
        y += lineHeight;
    }

    if (footer.line5) {
        doc.text(footer.line5, PAGE_LAYOUT.leftMargin, y);
    }
};

/**
 * Calculate subtotal from line items
 */
const calculateSubtotal = (items: InvoiceLineItem[]): number => {
    return items.reduce((sum, item) => sum + item.total, 0);
};

/**
 * Create an invoice PDF
 * @param data - Invoice data
 * @param xrechnungOptions - Optional XRechnung e-invoice options
 * @returns PDF blob, filename, and optionally XML content
 */
export const createInvoice = async (
    data: InvoiceData,
    xrechnungOptions?: XRechnungOptions
): Promise<InvoiceResult> => {
    try {
        // Create PDF document
        const doc = new jsPDF();
        await loadCustomFont(doc);

        // Header with logo (uses emitter website if available)
        let currentY = await addHeader(doc, data.emitter);

        // Add invoice date on right side (positioned at address block level)
        addInvoiceDate(doc, data.invoiceDate, currentY + 20);

        // Sender line and customer address (uses emitter data for sender line)
        currentY = addAddressBlock(doc, data.customer, currentY, data.emitter);
        currentY += 15;

        // Invoice title and details
        currentY = addInvoiceDetails(doc, data, currentY);

        // Greeting
        currentY = addGreeting(doc, currentY);

        // Line items
        currentY = addLineItems(doc, data.lineItems, currentY);
        currentY += 5;

        // Totals
        const subtotal = calculateSubtotal(data.lineItems);
        currentY = addTotals(doc, subtotal, data.vatRate, currentY);

        // Payment terms
        addPaymentTerms(doc, currentY, data.skonto_percentage, data.skonto_days, data.payment_days);

        // Footer (uses emitter data for company info)
        addFooter(doc, data.emitter);

        // Generate base PDF
        const fileName = `Rechnung_${data.invoiceNumber.replace('/', '-')}_${formatDateShort(data.invoiceDate).replace(/\./g, '-')}.pdf`;

        // Handle XRechnung XML generation if requested
        let xmlContent: string | undefined;
        let finalPdfBlob: Blob;

        if (xrechnungOptions?.generateXml) {
            // Map legacy data to XRechnung format
            const xrechnungData = mapLegacyInvoiceData(data, xrechnungOptions);

            // Validate XRechnung data
            const validationResult = validateXRechnungData(xrechnungData);
            if (!validationResult.valid) {
                console.warn('XRechnung validation warnings:\n', formatValidationResult(validationResult));
            }

            // Generate XML
            xmlContent = buildXRechnungXml(xrechnungData);
            console.log('XRechnung XML generated');

            if (xrechnungOptions.embedInPdf) {
                // Get PDF as ArrayBuffer for embedding
                const pdfArrayBuffer = doc.output('arraybuffer');

                // Embed XML in PDF
                const pdfWithXml = await embedXmlInPdf(pdfArrayBuffer, xmlContent, data.invoiceNumber);
                finalPdfBlob = new Blob([pdfWithXml], {type: 'application/pdf'});
                console.log('XRechnung XML embedded in PDF');
            } else {
                // Return PDF without embedded XML
                finalPdfBlob = doc.output('blob');
            }
        } else {
            // Standard PDF without XRechnung
            finalPdfBlob = doc.output('blob');
        }

        console.log('Invoice PDF generated:', fileName);

        return {pdfBlob: finalPdfBlob, fileName, xmlContent};

    } catch (error) {
        console.error('Error generating invoice PDF:', error);
        throw error;
    }
};

// Re-export XRechnung types for convenience
export type {XRechnungOptions, InvoiceResult} from './xrechnung';
